import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDiesel } from '../../context/DieselContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { useModuleFavicon } from '../../hooks/useModuleFavicon';
import {
    Home,
    Truck,
    Users,
    Fuel,
    AlertTriangle,
    BarChart3,
    Settings,
    Menu,
    X,
    LogOut,
    Sun,
    Moon,
    Bell,
    ChevronRight,
    Gauge,
    FileText,
} from 'lucide-react';
import { OfflineBanner, PendingBadge } from './OfflineBanner';

const navItems = [
    { 
        href: '/diesel/dashboard', 
        label: 'Dashboard', 
        icon: Home,
        permiso: 'ver_dashboard'
    },
    { 
        href: '/diesel/vehiculos', 
        label: 'Vehículos', 
        icon: Truck,
        permiso: 'ver_vehiculos'
    },
    { 
        href: '/diesel/conductores', 
        label: 'Conductores', 
        icon: Users,
        permiso: 'ver_conductores'
    },
    { 
        href: '/diesel/cargas', 
        label: 'Cargas', 
        icon: Fuel,
        permiso: 'ver_cargas'
    },
    { 
        href: '/diesel/cargas/nueva', 
        label: 'Nueva Carga', 
        icon: Gauge,
        permiso: 'registrar_cargas',
        highlight: true
    },
    { 
        href: '/diesel/alertas', 
        label: 'Alertas', 
        icon: AlertTriangle,
        permiso: 'ver_alertas'
    },
    { 
        href: '/diesel/reportes', 
        label: 'Reportes', 
        icon: BarChart3,
        permiso: 'ver_reportes'
    },
    { 
        href: '/diesel/configuracion', 
        label: 'Configuración', 
        icon: Settings,
        permiso: 'configurar_empresa'
    },
];

export function DieselLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { config, loading, hasPermission, nombreSistema, logoUrl } = useDiesel();
    const { isDark, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const prev = document.title;
        document.title = nombreSistema || 'Diesel - Control de Combustible';
        return () => { document.title = prev; };
    }, [nombreSistema]);
    useModuleFavicon('diesel');

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const visibleNavItems = navItems.filter(item => hasPermission(item.permiso));

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="space-y-4 w-64">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-32 w-full" />
                </div>
            </div>
        );
    }

    return (
        <div 
            className="min-h-screen bg-background diesel-theme"
            style={{
                '--diesel-primary': config?.color_primario || '#D4AF37',
                '--diesel-secondary': config?.color_secundario || '#1a1a1a',
                '--diesel-accent': config?.color_acento || '#ffffff',
            }}
        >
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-card border-b-2 border-border">
                <div className="flex items-center justify-between h-full px-4">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        {logoUrl ? (
                            <img src={logoUrl} alt={nombreSistema} className="h-8" />
                        ) : (
                            <div className="flex items-center gap-2">
                                <Fuel className="h-6 w-6" style={{ color: 'var(--diesel-primary)' }} />
                                <span className="font-heading font-black text-lg uppercase">
                                    {nombreSistema}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={toggleTheme}>
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleLogout}
                            className="text-destructive"
                        >
                            <LogOut className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div 
                    className="lg:hidden fixed inset-0 z-50 bg-black/50"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={cn(
                "fixed top-0 left-0 z-50 h-full w-72 bg-card border-r-2 border-border transition-transform duration-300",
                "lg:translate-x-0",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="h-16 flex items-center justify-between px-4 border-b border-border">
                        <Link to="/diesel/dashboard" className="flex items-center gap-3">
                            {logoUrl ? (
                                <img src={logoUrl} alt={nombreSistema} className="h-8" />
                            ) : (
                                <>
                                    <Fuel className="h-7 w-7" style={{ color: 'var(--diesel-primary)' }} />
                                    <span className="font-heading font-black text-xl uppercase tracking-tight">
                                        {nombreSistema}
                                    </span>
                                </>
                            )}
                        </Link>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Navigation */}
                    <ScrollArea className="flex-1 py-4">
                        <nav className="space-y-1 px-3">
                            {visibleNavItems.map((item) => {
                                const isActive = location.pathname === item.href || 
                                    (item.href !== '/diesel/dashboard' && location.pathname.startsWith(item.href));
                                const Icon = item.icon;

                                return (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        onClick={() => setSidebarOpen(false)}
                                        className={cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                                            isActive 
                                                ? "bg-[var(--diesel-primary)] text-[var(--diesel-secondary)] font-bold"
                                                : item.highlight
                                                    ? "bg-[var(--diesel-primary)]/10 text-[var(--diesel-primary)] hover:bg-[var(--diesel-primary)]/20 border-2 border-[var(--diesel-primary)]"
                                                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                        )}
                                    >
                                        <Icon className="h-5 w-5" />
                                        <span className="font-medium">{item.label}</span>
                                        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                                    </Link>
                                );
                            })}
                        </nav>
                    </ScrollArea>

                    {/* User Info */}
                    <div className="p-4 border-t border-border">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-[var(--diesel-primary)]/20 flex items-center justify-center">
                                <span className="text-sm font-bold" style={{ color: 'var(--diesel-primary)' }}>
                                    {user?.nombre?.charAt(0)?.toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{user?.nombre}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => navigate('/home')}
                            >
                                <Home className="h-4 w-4 mr-1" />
                                Inicio
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={toggleTheme}
                            >
                                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleLogout}
                                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="lg:ml-72 pt-16 lg:pt-0 min-h-screen">
                <div className="p-4 lg:p-6">
                    <div className="mb-4">
                        <OfflineBanner />
                    </div>
                    {children}
                </div>
            </main>
        </div>
    );
}
